import { useRoute } from 'vue-router'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { parseFullName, toApiDate } from '@/utils/formatters'
import type { IAudit } from '@/modules/audits/stores/useAuditRegisterStore/types/IAudit'
import type { IAuditEmployee } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditEmployee'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'

export function useAuditForm() {
  const route = useRoute()
  const auditRegisterStore = useAuditRegisterStore()
  const auditGroupStore = useAuditGroupStore()
  const feedbackStore = useFeedbackStore()

  const { currentAudit, editableAudit } = storeToRefs(auditRegisterStore)
  const { auditsGroup, currentGroup } = storeToRefs(auditGroupStore)

  const isEdit = computed(() => Boolean(route.params.id))

  const showDateTimeStartPicker = ref(false)
  const showDateTimeEndPicker = ref(false)
  const isEmployeeInfoModalOpen = ref(false)
  const selectedEmployeeId = ref('')

  const validationErrors = ref({
    name: false,
    planned_start: false,
    planned_end: false,
    departments: false,
    manager: false,
    audit_assignments: false,
  })

  const showEmployeeInfoModal = (id: string) => {
    if (!id) return
    selectedEmployeeId.value = id
    isEmployeeInfoModalOpen.value = true
  }

  const emptyAudit: IAudit = {
    audit_type: 'planned',
    name: '',
    departments: [],
    planned_start: '',
    planned_end: '',
    manager: null,
    audit_assignments: [],
  }

  const hasFormChanges = (): boolean => {
    if (!currentAudit.value) return false
    /*
     * Create: если код уже зафиксировал baseline в editableAudit (например,
     * автодата/отделение из календаря) — сравниваем с ним. Иначе — с пустой формой.
     * Edit: editableAudit = снимок с сервера.
     */
    const compareWith = route.params.id
      ? editableAudit.value
      : (editableAudit.value ?? emptyAudit)

    if (!compareWith) return false

    return JSON.stringify(currentAudit.value) !== JSON.stringify(compareWith)
  }

  const selectedDepartmentIds = computed(() =>
    currentAudit.value.departments?.map(d => d.id) ?? [],
  )

  const onDepartmentsUpdate = (ids: string[]) => {
    currentAudit.value.departments = ids.map(id => ({ id }))
  }

  const auditGroupOptions = computed(() =>
    auditsGroup.value.map(g => ({ title: g.name, value: g.id })),
  )

  const onAuditGroupSelect = async (id: number | string | null) => {
    if (!id) return

    await auditGroupStore.fetchAuditGroupById(Number(id))

    const group = auditGroupStore.currentGroup
    if (!group || Number(group.id) !== Number(id)) return

    for (const ga of group.group_assignments) {
      const existing = currentAudit.value.audit_assignments.find(
        a => a.employee.id === ga.employee.id,
      )
      if (existing) {
        for (const p of ga.points) {
          if (!existing.points.some(ep => ep.id === p.id)) {
            existing.points.push({ id: p.id, label: p.label })
          }
        }
      } else {
        currentAudit.value.audit_assignments.push({
          employee: { ...ga.employee },
          points: ga.points.map(p => ({ id: p.id, label: p.label })),
        })
      }
    }

    auditRegisterStore.clearTreeChoices('audit_criterion')
  }

  const addAuditParticipant = (employee: IEmployeeTreeItem) => {
    const isAlreadyAdded = currentAudit.value.audit_assignments.some(
      a => a.employee.id === employee.id,
    )
    if (isAlreadyAdded) return

    const parsed = parseFullName(employee.full_name)

    currentAudit.value.audit_assignments.push({
      employee: {
        id: employee.id,
        first_name: parsed.first_name,
        last_name: parsed.last_name,
        middle_name: parsed.middle_name,
      },
      points: [],
    })
  }

  const addAuditParticipants = (employees: IEmployeeTreeItem[]) => {
    const existingAssignments = [...currentAudit.value.audit_assignments]

    currentAudit.value.audit_assignments = employees.map(emp => {
      const existing = existingAssignments.find(a => a.employee.id === emp.id)
      if (existing) return existing

      const parsed = parseFullName(emp.full_name)

      return {
        employee: {
          id: emp.id,
          first_name: parsed.first_name,
          last_name: parsed.last_name,
          middle_name: parsed.middle_name,
          position_name: emp.position_name,
        },
        points: [],
      }
    })
  }

  const copyPointsToOthers = (sourceEmployeeId: string) => {
    const sourceAssignment = currentAudit.value.audit_assignments.find(a => a.employee.id === sourceEmployeeId)
    if (!sourceAssignment || !sourceAssignment.points?.length) return

    const source = sourceAssignment.points.map(point => ({ id: point.id, label: point.label }))

    currentAudit.value.audit_assignments.forEach(assignment => {
      if (assignment.employee.id === sourceEmployeeId) return
      if (assignment.can_edit === false) return

      const currentPoints = assignment.points ?? []
      const existingIds = new Set(currentPoints.map(point => point.id))
      const added = source
        .filter(point => !existingIds.has(point.id))
        .map(point => ({ ...point }))

      assignment.points = [...currentPoints, ...added]
    })
  }

  const removeAuditParticipant = (employeeId: string) => {
    const index = currentAudit.value.audit_assignments.findIndex(
      a => a.employee.id === employeeId,
    )
    if (index === -1) return
    if (currentAudit.value.audit_assignments[index].can_edit === false) return
    currentAudit.value.audit_assignments.splice(index, 1)
  }

  const updateParticipantPoints = (
    employeeId: string,
    points: { id: string; label?: string }[],
  ) => {
    const assignment = currentAudit.value.audit_assignments.find(
      a => a.employee.id === employeeId,
    )
    if (assignment && assignment.can_edit !== false) {
      assignment.points = points
    }
  }

  const transferPoints = (
    fromEmployeeId: string,
    toEmployeeId: string,
    pointIds: string[],
  ) => {
    const from = currentAudit.value.audit_assignments.find(
      a => a.employee.id === fromEmployeeId,
    )
    const to = currentAudit.value.audit_assignments.find(
      a => a.employee.id === toEmployeeId,
    )
    if (!from || !to || from.can_edit === false || to.can_edit === false) return

    const toTransfer = from.points.filter(p => pointIds.includes(p.id))
    from.points = from.points.filter(p => !pointIds.includes(p.id))

    for (const point of toTransfer) {
      if (!to.points.some(p => p.id === point.id)) {
        to.points.push(point)
      }
    }
  }

  const setManager = (employee: IEmployeeTreeItem | IAuditEmployee) => {
    const emp = employee as any
    const hasFullName = 'full_name' in employee
    const parsed = hasFullName
      ? parseFullName(emp.full_name)
      : {
          first_name: emp.first_name || '',
          last_name: emp.last_name || '',
          middle_name: emp.middle_name || '',
        }

    currentAudit.value.manager = {
      id: employee.id,
      first_name: parsed.first_name,
      last_name: parsed.last_name,
      middle_name: parsed.middle_name,
    }
  }

  const clearManager = () => {
    currentAudit.value.manager = null
  }

  const onStartDateSave = (dateValue: string | Date | null) => {
    if (!dateValue) return
    const dateStr = typeof dateValue === 'string' ? dateValue : toApiDate(dateValue)
    if (!dateStr) return

    if (currentAudit.value.planned_end) {
      const endStr = typeof currentAudit.value.planned_end === 'string'
        ? currentAudit.value.planned_end
        : toApiDate(currentAudit.value.planned_end)
      if (endStr && dateStr > endStr) {
        feedbackStore.showToast({
          type: 'warning',
          message: 'Дата начала позже даты окончания. Дата окончания сброшена.',
          time: 5000,
        })
        currentAudit.value.planned_end = ''
      }
    }
    currentAudit.value.planned_start = dateStr
    showDateTimeStartPicker.value = false
  }

  const onEndDateSave = (dateValue: string | Date | null) => {
    if (!dateValue) return
    const dateStr = typeof dateValue === 'string' ? dateValue : toApiDate(dateValue)
    if (!dateStr) return

    const startStr = currentAudit.value.planned_start
      ? (typeof currentAudit.value.planned_start === 'string'
          ? currentAudit.value.planned_start
          : toApiDate(currentAudit.value.planned_start))
      : null
    if (startStr && dateStr < startStr) {
      feedbackStore.showToast({
        type: 'error',
        message: 'Дата окончания должна быть позже даты начала',
        time: 5000,
      })
      currentAudit.value.planned_end = ''
      showDateTimeEndPicker.value = false
      return
    }
    currentAudit.value.planned_end = dateStr
    showDateTimeEndPicker.value = false
  }

  const validateForm = () => {
    validationErrors.value = {
      name: !currentAudit.value.name,
      planned_start: !currentAudit.value.planned_start,
      planned_end: !currentAudit.value.planned_end,
      departments: !currentAudit.value.departments.length,
      manager: !currentAudit.value.manager?.id,
      audit_assignments: !currentAudit.value.audit_assignments.length,
    }
    return !Object.values(validationErrors.value).some(Boolean)
  }

  const onAuditFormSubmit = async () => {
    if (!validateForm()) {
      feedbackStore.showToast({
        type: 'error',
        message: 'Заполните все обязательные поля',
        time: 5000,
      })
      return false
    }
    const id = Number(route.params.id)
    if (isEdit.value && id) {
      await auditRegisterStore.updateAudit(id)
    } else {
      await auditRegisterStore.createAudit()
    }
    return true
  }

  return {
    isEdit,
    currentAudit,
    validationErrors,
    showDateTimeStartPicker,
    showDateTimeEndPicker,
    onStartDateSave,
    onEndDateSave,
    selectedDepartmentIds,
    onDepartmentsUpdate,
    auditGroupOptions,
    onAuditGroupSelect,
    onAuditFormSubmit,
    hasFormChanges,
    currentGroup,
    isEmployeeInfoModalOpen,
    selectedEmployeeId,
    showEmployeeInfoModal,
    addAuditParticipant,
    addAuditParticipants,
    copyPointsToOthers,
    removeAuditParticipant,
    updateParticipantPoints,
    transferPoints,
    setManager,
    clearManager,
  }
}
